class TerminalStore {
  constructor() {
    this.commandHistory = [];
    this.historyIndex = -1;
    this.products = [];
    this.articles = [];
    this.cart = [];
    this.currentCommand = '';
    this.isModalOpen = false;
    this.isMobile = this.detectMobile();
    
    this.init();
  }

  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }

  init() {
    this.setupEventListeners();
    this.loadProducts();
    this.loadArticles();
    this.displayWelcome();
    this.focusInput();

    // Mark output as a live region so screen readers announce new lines
    const output = document.getElementById('terminal-output');
    if (output) {
      output.setAttribute('role', 'log');
      output.setAttribute('aria-live', 'polite');
      output.setAttribute('aria-relevant', 'additions');
    }
  }

  setupEventListeners() {
    const input = document.getElementById('terminal-input');
    const terminal = document.getElementById('terminal-window');

    if (input) {
      input.addEventListener('keydown', (e) => this.handleKeyDown(e));
      input.addEventListener('input', (e) => this.handleInput(e));
    }

    // Focus terminal on click
    if (terminal) {
      terminal.addEventListener('click', () => this.focusInput());
    }

    // Handle modal close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen) {
        this.closeModal();
      }
    });

    // Mode-toggle clicks are handled at the layout level (works on all pages,
    // including those that don't load terminal.js). We just listen for the
    // custom event to react in-terminal.
    document.addEventListener('grind:modechange', (e) => {
      const m = e.detail && e.detail.mode;
      if (m === 'shop') {
        const shop = document.querySelector('.shop-container');
        if (shop) shop.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  handleKeyDown(e) {
    switch(e.key) {
      case 'Enter':
        e.preventDefault();
        this.executeCommand();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.navigateHistory(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.navigateHistory(1);
        break;
      case 'Tab':
        e.preventDefault();
        this.autoComplete();
        break;
    }
  }

  handleInput(e) {
    this.currentCommand = e.target.value;
  }

  navigateHistory(direction) {
    if (this.commandHistory.length === 0) return;
    
    this.historyIndex += direction;
    
    if (this.historyIndex < 0) {
      this.historyIndex = 0;
    } else if (this.historyIndex >= this.commandHistory.length) {
      this.historyIndex = this.commandHistory.length - 1;
    }
    
    const input = document.getElementById('terminal-input');
    if (input) {
      input.value = this.commandHistory[this.historyIndex] || '';
      this.currentCommand = input.value;
    }
  }

  autoComplete() {
    const commands = ['help', 'ls', 'products', 'view', 'add', 'cart', 'checkout', 'clear', 'search', 'contact', 'home', 'shop', 'gui', 'terminal', 'cli', 'about', 'field', 'blog', 'blogs', 'read'];
    const input = document.getElementById('terminal-input');
    
    if (!input) return;
    
    const currentValue = input.value.toLowerCase().trim();
    const matches = commands.filter(cmd => cmd.startsWith(currentValue));
    
    if (matches.length === 1) {
      input.value = matches[0] + ' ';
      this.currentCommand = input.value;
    } else if (matches.length > 1) {
      this.addOutput(`Available commands: ${matches.join(', ')}`);
    }
  }

  executeCommand() {
    const command = this.currentCommand.trim();
    if (!command) return;
    
    this.addToHistory(command);
    this.addOutput(`> ${command}`);
    
    const parts = command.split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);
    
    switch(cmd) {
      case 'help':
        this.showHelp();
        break;
      case 'ls':
      case 'products':
        this.listProducts();
        break;
      case 'view':
        this.viewProduct(args.join(' '));
        break;
      case 'add':
        this.addToCart(args.join(' '));
        break;
      case 'cart':
        this.showCart();
        break;
      case 'remove':
        this.removeFromCart(args[0]);
        break;
      case 'checkout':
        this.checkout();
        break;
      case 'clear':
        this.clearTerminal();
        break;
      case 'search':
        this.searchProducts(args.join(' '));
        break;
      case 'contact':
        this.showContact();
        break;
      case 'about':
        this.showAbout();
        break;
      case 'field':
      case 'fieldnotes':
        this.showFieldNotes();
        break;
      case 'blog':
      case 'blogs':
        this.listArticles();
        break;
      case 'read':
        this.readArticle(args.join(' '));
        break;
      case 'home':
        window.location.href = '/';
        break;
      case 'shop':
      case 'gui':
        this.switchMode('shop');
        break;
      case 'terminal':
      case 'cli':
        this.switchMode('terminal');
        break;
      default:
        this.addOutput(`Command not found: ${cmd}. Type 'help' for available commands.`, 'error');
    }
    
    this.clearInput();
    
    // Mobile: refocus input after command execution
    if (this.isMobile) {
      setTimeout(() => {
        this.focusInput();
        const input = document.getElementById('terminal-input');
        if (input) {
          input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  }

  addToHistory(command) {
    this.commandHistory.push(command);
    this.historyIndex = this.commandHistory.length;
  }

  addOutput(text, type = 'normal') {
    const output = document.getElementById('terminal-output');
    if (!output) return;
    
    const line = document.createElement('div');
    line.className = `terminal-line ${type}`;
    line.textContent = text;
    
    output.appendChild(line);
    output.scrollTop = output.scrollHeight;
  }

  clearInput() {
    const input = document.getElementById('terminal-input');
    if (input) {
      input.value = '';
      this.currentCommand = '';
    }
  }

  focusInput() {
    const input = document.getElementById('terminal-input');
    if (input) {
      input.focus();
    }
  }

  brand() {
    const b = window.GRIND_BRAND || {};
    return {
      fullName: (b.fullName || 'General Relativity Industries').toUpperCase(),
      abbr: b.abbr || 'G.R. IND.'
    };
  }

  displayWelcome() {
    const output = document.getElementById('terminal-output');
    if (!output) return;

    // Home page — terminal-output is empty, run boot sequence typewriter.
    // Other pages have Liquid-rendered output already; skip the sequence.
    if (output.children.length === 0) {
      this.runBootSequence();
    }
  }

  runBootSequence() {
    const b = this.brand();
    const lines = [
      '╔══════════════════════════════════════════════════════════════╗',
      '║                                                              ║',
      this.padCentered(b.fullName, 62),
      this.padCentered(b.abbr, 62),
      '║                                                              ║',
      '║  Type "help" to see available commands                       ║',
      '║  Type "ls" to browse inventory                               ║',
      '║  Type "shop" for the conventional grid view                  ║',
      '╚══════════════════════════════════════════════════════════════╝',
      '',
      `> SYSTEM BOOT :: ${new Date().toISOString().slice(0, 19).replace('T', ' ')}`,
      `> ${b.abbr} Terminal initialized. Ready for commands.`,
    ];

    // Hide input cursor until boot finishes
    const input = document.getElementById('terminal-input');
    const cursor = document.querySelector('.terminal-cursor');
    if (input) input.disabled = true;
    if (cursor) cursor.style.opacity = '0';

    let i = 0;
    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    const printLines = async () => {
      for (const line of lines) {
        await delay(i === 0 ? 80 : 55);
        this.addOutput(line, line.startsWith('>') ? 'info' : 'normal');
        i++;
      }
      await delay(180);
      if (input) input.disabled = false;
      if (cursor) cursor.style.opacity = '';
      this.focusInput();
    };

    printLines();
  }

  padCentered(text, innerWidth) {
    const trimmed = String(text).slice(0, innerWidth);
    const pad = Math.max(0, innerWidth - trimmed.length);
    const left = Math.floor(pad / 2);
    const right = pad - left;
    return '║' + ' '.repeat(left) + trimmed + ' '.repeat(right) + '║';
  }

  showHelp() {
    const b = this.brand();
    this.addOutput(`${b.fullName} (${b.abbr}) — Available Commands:`);
    this.addOutput('');
    this.addOutput('  help                    - Show this help message');
    this.addOutput('  ls, products            - List all available products');
    this.addOutput('  view <product>          - View detailed product information');
    this.addOutput('  add <product>           - Add product to cart');
    this.addOutput('  cart                    - View shopping cart');
    this.addOutput('  remove <item>           - Remove item from cart');
    this.addOutput('  checkout                - Proceed to checkout');
    this.addOutput('  search <keyword>        - Search products by keyword');
    this.addOutput('  contact                 - Show contact information');
    this.addOutput('  about                   - About the company');
    this.addOutput('  field                   - Field notes for the current product');
    this.addOutput('  blog, blogs             - List blog articles');
    this.addOutput('  read <article>          - Open an article (number or slug)');
    this.addOutput('  shop, gui               - Switch to conventional grid view');
    this.addOutput('  terminal, cli           - Switch back to terminal view');
    this.addOutput('  home                    - Return to homepage');
    this.addOutput('  clear                   - Clear terminal screen');
    this.addOutput('');
    this.addOutput('Examples:');
    this.addOutput('  view product-name');
    this.addOutput('  add 1');
    this.addOutput('  search keyword');
    this.addOutput('  read 1');
  }

  showContact() {
    const b = this.brand();
    this.addOutput('╔══════════════════════════════════════════════════════════════╗');
    this.addOutput(this.padCentered(b.fullName, 62));
    this.addOutput(this.padCentered(b.abbr, 62));
    this.addOutput('╚══════════════════════════════════════════════════════════════╝');
    this.addOutput('');
    this.addOutput('> For full contact details, visit /pages/contact', 'info');
  }

  showAbout() {
    const b = this.brand();
    this.addOutput(`${b.fullName} (${b.abbr})`);
    this.addOutput('');
    this.addOutput('Physics-grade apparel and gear, engineered with the rigor of the universe.');
    this.addOutput('Type "shop" for the visual catalog, or "ls" to list products via CLI.');
  }

  showFieldNotes() {
    const p = window.GRIND_PRODUCT;
    if (!p || !p.fieldNotes) {
      this.addOutput('No field notes available here. Try this command on a product page.', 'info');
      return;
    }
    this.addOutput(`> FIELD NOTES // ${String(p.title).toUpperCase()}`);
    this.addOutput('+--------------------------------+');
    String(p.fieldNotes)
      .split(/\r?\n/)
      .forEach((line) => this.addOutput(line));
    this.addOutput('+--------------------------------+');
  }

  switchMode(target) {
    if (target !== 'shop' && target !== 'terminal') return;
    this.addOutput(target === 'shop'
      ? '> Switching to SHOP MODE :: scroll down for the grid view.'
      : '> Switching to TERMINAL MODE.', 'success');
    if (window.GRIND && typeof window.GRIND.setMode === 'function') {
      window.GRIND.setMode(target);
    }
  }

  async loadProducts() {
    try {
      // Load from actual Shopify products passed via Liquid
      if (window.terminalProducts && window.terminalProducts.length > 0) {
        this.products = window.terminalProducts;
      } else {
        // Fetch from Shopify API if not pre-loaded
        const response = await fetch('/products.json');
        const data = await response.json();
        this.products = data.products.map(product => ({
          id: product.id,
          title: product.title,
          price: product.variants[0].price / 100, // Convert from cents
          description: product.body_html ? product.body_html.replace(/<[^>]*>/g, '') : '',
          image: product.images[0]?.src || '',
          variants: product.variants.map(variant => ({
            id: variant.id,
            title: variant.title,
            price: variant.price / 100
          }))
        }));
      }
    } catch (error) {
      this.addOutput('Error loading products. Please try again later.', 'error');
    }
  }

  listProducts() {
    if (this.products.length === 0) {
      this.addOutput('No products available at this time.');
      return;
    }
    
    this.addOutput('Available Products:');
    this.addOutput('');
    
    this.products.forEach((product, index) => {
      this.addOutput(`${index + 1}. ${product.title} - $${product.price}`);
      this.addOutput(`   ${product.description}`, 'info');
      this.addOutput('');
    });
    
    this.addOutput('Use "view <product>" or "view <number>" to see details');
  }

  viewProduct(productQuery) {
    if (!productQuery) {
      this.addOutput('Usage: view <product> or view <number>');
      return;
    }
    
    let product = null;
    
    // Check if it's a number (product index)
    const index = parseInt(productQuery) - 1;
    if (!isNaN(index) && index >= 0 && index < this.products.length) {
      product = this.products[index];
    } else {
      // Search by name
      product = this.products.find(p => 
        p.title.toLowerCase().includes(productQuery.toLowerCase())
      );
    }
    
    if (!product) {
      this.addOutput(`Product not found: ${productQuery}`, 'error');
      return;
    }
    
    this.openProductModal(product);
  }

  openProductModal(product) {
    this.isModalOpen = true;
    this._modalTrigger = document.activeElement;

    const safeTitle = String(product.title).replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const safeDesc = String(product.description || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const safeImage = String(product.image || '');
    const safePrice = parseFloat(product.price).toFixed(2);

    const modal = document.createElement('div');
    modal.className = 'terminal-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'modal-product-title');

    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title" id="modal-product-title">${safeTitle}</div>
          <button class="modal-close" id="modal-close-btn" aria-label="Close product detail">CLOSE [X]</button>
        </div>
        <div class="modal-body">
          ${safeImage ? `<img src="${safeImage}" alt="${safeTitle}" class="product-image">` : ''}
          <div class="product-details">
            <div class="product-title" aria-hidden="true">${safeTitle}</div>
            <div class="product-price-large">$${safePrice}</div>
            <div class="product-description-large">${safeDesc}</div>

            ${product.variants.length > 1 ? `
              <div class="variant-selector">
                <span class="variant-label" id="variant-label">Variant:</span>
                <div class="variant-options" role="group" aria-labelledby="variant-label">
                  ${product.variants.map(v => `
                    <button class="variant-option" data-variant-id="${v.id}" aria-pressed="false">${String(v.title).replace(/</g, '&lt;')}</button>
                  `).join('')}
                </div>
              </div>
            ` : ''}

            <div class="quantity-selector">
              <label class="variant-label" for="modal-qty">Quantity:</label>
              <input type="number" id="modal-qty" class="quantity-input" value="1" min="1" max="10">
            </div>

            <button class="add-to-cart-btn" id="modal-add-btn">[ ADD TO CART ]</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close button
    modal.querySelector('#modal-close-btn').addEventListener('click', () => this.closeModal());

    // Add to cart button
    modal.querySelector('#modal-add-btn').addEventListener('click', () => this.addProductToCart(product.id));

    // Variant selection
    const variantOptions = modal.querySelectorAll('.variant-option');
    variantOptions.forEach(option => {
      option.addEventListener('click', () => {
        variantOptions.forEach(opt => { opt.classList.remove('selected'); opt.setAttribute('aria-pressed', 'false'); });
        option.classList.add('selected');
        option.setAttribute('aria-pressed', 'true');
      });
    });
    if (variantOptions.length > 0) {
      variantOptions[0].classList.add('selected');
      variantOptions[0].setAttribute('aria-pressed', 'true');
    }

    // Focus first interactive element
    const closeBtn = modal.querySelector('#modal-close-btn');
    if (closeBtn) closeBtn.focus();

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeModal();
    });
  }

  closeModal() {
    const modal = document.querySelector('.terminal-modal');
    if (modal) {
      modal.remove();
      this.isModalOpen = false;
      if (this._modalTrigger && typeof this._modalTrigger.focus === 'function') {
        this._modalTrigger.focus();
      } else {
        this.focusInput();
      }
      this._modalTrigger = null;
    }
  }

  addProductToCart(productId) {
    const product = this.products.find(p => p.id === productId);
    if (!product) return;
    
    const modal = document.querySelector('.terminal-modal');
    const selectedVariant = modal.querySelector('.variant-option.selected');
    const quantity = modal.querySelector('.quantity-input').value;
    
    const variantId = selectedVariant ? selectedVariant.dataset.variantId : product.variants[0].id;
    const variant = product.variants.find(v => v.id == variantId);
    
    const cartItem = {
      id: `${productId}-${variantId}`,
      productId: productId,
      variantId: variantId,
      title: product.title,
      variant: variant.title,
      price: variant.price,
      quantity: parseInt(quantity),
      image: product.image
    };
    
    // Check if item already exists in cart
    const existingItem = this.cart.find(item => item.id === cartItem.id);
    if (existingItem) {
      existingItem.quantity += cartItem.quantity;
    } else {
      this.cart.push(cartItem);
    }
    
    this.addOutput(`Added ${cartItem.quantity}x ${cartItem.title} (${cartItem.variant}) to cart`, 'success');
    this.closeModal();
  }

  addToCart(productQuery) {
    if (!productQuery) {
      this.addOutput('Usage: add <product> or add <number>');
      return;
    }
    
    let product = null;
    
    // Check if it's a number (product index)
    const index = parseInt(productQuery) - 1;
    if (!isNaN(index) && index >= 0 && index < this.products.length) {
      product = this.products[index];
    } else {
      // Search by name
      product = this.products.find(p => 
        p.title.toLowerCase().includes(productQuery.toLowerCase())
      );
    }
    
    if (!product) {
      this.addOutput(`Product not found: ${productQuery}`, 'error');
      return;
    }
    
    this.openProductModal(product);
  }

  showCart() {
    if (this.cart.length === 0) {
      this.addOutput('Your cart is empty.');
      return;
    }
    
    this.addOutput('Shopping Cart:');
    this.addOutput('');
    
    let total = 0;
    this.cart.forEach((item, index) => {
      const itemTotal = item.price * item.quantity;
      total += itemTotal;
      
      this.addOutput(`${index + 1}. ${item.title} (${item.variant})`);
      this.addOutput(`   Quantity: ${item.quantity} x $${item.price} = $${itemTotal.toFixed(2)}`, 'info');
      this.addOutput('');
    });
    
    this.addOutput(`Total: $${total.toFixed(2)}`, 'success');
    this.addOutput('');
    this.addOutput('Use "remove <number>" to remove items or "checkout" to proceed');
  }

  removeFromCart(itemIndex) {
    const index = parseInt(itemIndex) - 1;
    if (isNaN(index) || index < 0 || index >= this.cart.length) {
      this.addOutput(`Invalid item number: ${itemIndex}`, 'error');
      return;
    }
    
    const item = this.cart[index];
    this.cart.splice(index, 1);
    this.addOutput(`Removed ${item.title} from cart`, 'success');
  }

  checkout() {
    this.addOutput('Redirecting to /cart for secure checkout...', 'info');
    setTimeout(() => { window.location.href = '/cart'; }, 600);
  }

  searchProducts(keyword) {
    if (!keyword) {
      this.addOutput('Usage: search <keyword>');
      return;
    }
    
    const results = this.products.filter(product => 
      product.title.toLowerCase().includes(keyword.toLowerCase()) ||
      product.description.toLowerCase().includes(keyword.toLowerCase())
    );
    
    if (results.length === 0) {
      this.addOutput(`No products found matching "${keyword}"`);
      return;
    }
    
    this.addOutput(`Search results for "${keyword}":`);
    this.addOutput('');
    
    results.forEach((product, index) => {
      this.addOutput(`${index + 1}. ${product.title} - $${product.price}`);
      this.addOutput(`   ${product.description}`, 'info');
      this.addOutput('');
    });
  }

  loadArticles() {
    // Articles are populated server-side via window.GRIND.articles on blog/article templates.
    // No /articles.json fallback exists on Shopify storefronts — silently no-op when empty.
    this.articles = (window.GRIND && Array.isArray(window.GRIND.articles)) ? window.GRIND.articles : [];
  }

  listArticles() {
    if (!this.articles || this.articles.length === 0) {
      this.addOutput('No articles loaded. Visit /blogs/<handle> to browse the blog.');
      return;
    }

    this.addOutput('Articles:');
    this.addOutput('');

    this.articles.forEach((article, index) => {
      const date = article.published_at || '';
      this.addOutput(`${index + 1}. ${article.title}${date ? '  [' + date + ']' : ''}`);
      if (article.excerpt) {
        this.addOutput(`   ${article.excerpt}`, 'info');
      }
      this.addOutput('');
    });

    this.addOutput('Use "read <number>" or "read <slug>" to open an article.');
  }

  readArticle(query) {
    if (!this.articles || this.articles.length === 0) {
      this.addOutput('No articles loaded. Visit /blogs/<handle> to browse the blog.');
      return;
    }

    if (!query) {
      this.addOutput('Usage: read <number> or read <slug>');
      return;
    }

    let article = null;
    const index = parseInt(query, 10) - 1;
    if (!isNaN(index) && index >= 0 && index < this.articles.length) {
      article = this.articles[index];
    } else {
      const q = query.toLowerCase();
      article = this.articles.find(a =>
        (a.handle && a.handle.toLowerCase() === q) ||
        (a.title && a.title.toLowerCase().includes(q))
      );
    }

    if (!article) {
      this.addOutput(`Article not found: ${query}`, 'error');
      return;
    }

    this.addOutput(`Opening: ${article.title}`, 'success');
    setTimeout(() => { window.location.href = article.url; }, 400);
  }

  clearTerminal() {
    const output = document.getElementById('terminal-output');
    if (output) {
      output.innerHTML = '';
    }
    this.displayWelcome();
  }
}

// Initialize only on pages that have a terminal input
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('terminal-input')) {
    window.terminalStore = new TerminalStore();
  }
});
